import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#loadMore);

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#loadMore);
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  #loadingMore = false;

  /** @param {MouseEvent} event */
  #loadMore = async (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('[data-load-more-next]');
    if (!link || !this.hasAttribute('data-manual-pagination') || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (this.#loadingMore) return;
    const grid = this.refs.grid;
    if (!grid) return;
    const url = new URL(link.href, window.location.href);
    const initialUrl = window.location.href;
    this.#loadingMore = true;
    link.setAttribute('aria-disabled', 'true');
    grid.setAttribute('aria-busy', 'true');
    try {
      const html = await sectionRenderer.getSectionHTML(this.sectionId, true, url);
      if (!this.isConnected || this.refs.grid !== grid || window.location.href !== initialUrl) return;
      const page = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = page.querySelector('[ref="grid"]');
      const pagination = page.querySelector('.orr-collection-pagination');
      if (!nextGrid || !pagination) throw new Error('Collection page unavailable');
      const existing = new Set([...grid.children].map(card => card.dataset.productId));
      const cards = [...nextGrid.children].filter(card => !existing.has(card.dataset.productId));
      if (!cards.length) throw new Error('Collection page contains no new products');
      grid.append(...cards);
      this.querySelector('.orr-collection-pagination')?.replaceWith(pagination);
      cards[0].querySelector('a')?.focus({ preventScroll: true });
    } catch {
      if (this.isConnected && this.refs.grid === grid && window.location.href === initialUrl) window.location.assign(url.href);
    } finally {
      this.#loadingMore = false;
      grid.removeAttribute('aria-busy');
      link.removeAttribute('aria-disabled');
    }
  };

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}

